import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { Button, Pagination, Spin, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { ConceptSearchBrief } from "fin-web-components";
import { ConceptDropSelect, StockLink, StockPrice, ratioToStockPricePct } from "fin-web-components";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useStockLinkDetail, stockLinkDetailPropsForCode } from "../../components/fin-web/useStockLinkDetail";
import { ConceptApi } from "./ConceptApi";
import { sortConceptStockRows } from "./ConceptSort";
import type {
  ConceptBrief,
  ConceptSortField,
  ConceptSortMode,
  ConceptSortOrder,
  ConceptStockRow,
} from "./ConceptType";
import "./ConceptPage.css";

export interface ConceptPageProps {
  spaceId: string;
  spaceProductId: string;
}

const PAGE_SIZE = 20;

function formatMarketValue(yuan: number): string {
  if (!Number.isFinite(yuan)) return "—";
  return `${(yuan / 100000000).toFixed(2)}亿`;
}

function formatPeRatio(v: number): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function formatDividendYield(v: number): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(2)}%`;
}

function FinNum(props: { children: ReactNode }): JSX.Element {
  return <span className="concept-page__fin-num">{props.children}</span>;
}

interface SortHeaderProps {
  label: string;
  field: ConceptSortField;
  active: boolean;
  order: ConceptSortOrder;
  onToggle: (field: ConceptSortField) => void;
}

function SortHeader(props: SortHeaderProps): JSX.Element {
  const { label, field, active, order, onToggle } = props;
  const handleToggle = () => onToggle(field);
  return (
    <span className="concept-page__sort-th">
      <span className="concept-page__sort-label" onClick={handleToggle}>
        {label}
      </span>
      <button
        type="button"
        className="concept-page__sort-btn"
        aria-label={`按${label}排序`}
        onClick={handleToggle}
      >
        <CaretUpOutlined
          className={
            active && order === "asc"
              ? "concept-page__sort-icon concept-page__sort-icon--active"
              : "concept-page__sort-icon"
          }
        />
        <CaretDownOutlined
          className={
            active && order === "desc"
              ? "concept-page__sort-icon concept-page__sort-icon--active"
              : "concept-page__sort-icon"
          }
        />
      </button>
    </span>
  );
}

export default function ConceptPage(props: ConceptPageProps): JSX.Element {
  const { spaceProductId } = props;
  const [searchParams] = useSearchParams();
  const initialConceptCode = searchParams.get("concept")?.trim() || "";
  const [conceptOptions, setConceptOptions] = useState<ConceptBrief[]>([]);
  const [conceptCodes, setConceptCodes] = useState<string[]>(() =>
    initialConceptCode ? [initialConceptCode] : []
  );
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ConceptStockRow[]>([]);
  const [sortMode, setSortMode] = useState<ConceptSortMode>("dragon");
  const [sortOrder, setSortOrder] = useState<ConceptSortOrder>("desc");
  const [page, setPage] = useState(1);
  const pendingAutoSearchRef = useRef(false);

  const { stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps } =
    useStockLinkDetail(props.spaceId);

  useEffect(() => {
    void (async () => {
      try {
        const list = await ConceptApi.listConcepts(spaceProductId);
        setConceptOptions(list);
      } catch {
        setConceptOptions([]);
      }
    })();
  }, [spaceProductId]);

  const runSearch = useCallback(async () => {
    if (conceptCodes.length === 0) {
      setRows([]);
      setPage(1);
      return;
    }
    setLoading(true);
    try {
      const data = await ConceptApi.searchStocks({
        spaceProductId,
        conceptCodes,
      });
      setRows(data);
      setPage(1);
      setSortMode("dragon");
      setSortOrder("desc");
    } catch (e) {
      message.error(e instanceof Error ? e.message : "查询失败");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [conceptCodes, spaceProductId]);

  useEffect(() => {
    const code = searchParams.get("concept")?.trim();
    const auto = searchParams.get("search") === "1";
    if (!code) {
      return;
    }
    setConceptCodes([code]);
    if (auto) {
      pendingAutoSearchRef.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (!pendingAutoSearchRef.current || conceptCodes.length === 0) {
      return;
    }
    pendingAutoSearchRef.current = false;
    void runSearch();
  }, [conceptCodes, runSearch]);

  const sortedRows = useMemo(
    () => sortConceptStockRows(rows, sortMode, sortOrder),
    [rows, sortMode, sortOrder]
  );

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, page]);

  const handleSortToggle = useCallback(
    (field: ConceptSortField) => {
      setPage(1);
      if (sortMode === field) {
        setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
      } else {
        setSortMode(field);
        setSortOrder("desc");
      }
    },
    [sortMode]
  );

  const applyDragonSort = useCallback(() => {
    setSortMode("dragon");
    setSortOrder("desc");
    setPage(1);
  }, []);

  const columns: ColumnsType<ConceptStockRow> = useMemo(
    () => [
      {
        title: "股票标的",
        key: "stock",
        align: "left",
        width: 180,
        render: (_: unknown, row: ConceptStockRow) => {
          const code = row.stockCode.trim();
          return (
            <StockLink
              stockCode={code}
              stockName={row.stockName}
              stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
              detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
              {...stockLinkDetailPropsForCode(stockLinkDetail, code)}
              onRequestDetail={requestStockDetailForLink}
              stockNotesTab={buildStockNotesTabProps(code, row.stockName)}
            />
          );
        },
      },
      {
        title: (
          <SortHeader
            label="涨跌幅"
            field="pctChange"
            active={sortMode === "pctChange"}
            order={sortOrder}
            onToggle={handleSortToggle}
          />
        ),
        key: "pctChange",
        align: "center",
        width: 180,
        render: (_: unknown, row: ConceptStockRow) => (
          <StockPrice
            price={row.latestPrice}
            change={
              row.pctChange != null && Number.isFinite(row.pctChange)
                ? ratioToStockPricePct(row.pctChange)
                : Number.NaN
            }
            mode="one-line"
          />
        ),
      },
      {
        title: (
          <SortHeader
            label="基本面分"
            field="fundamentalScore"
            active={sortMode === "fundamentalScore"}
            order={sortOrder}
            onToggle={handleSortToggle}
          />
        ),
        key: "fundamentalScore",
        dataIndex: "fundamentalScore",
        align: "center",
        render: (v: number) => (Number.isFinite(v) ? v.toFixed(1) : "—"),
      },
      {
        title: (
          <SortHeader
            label="位置分"
            field="positionScore"
            active={sortMode === "positionScore"}
            order={sortOrder}
            onToggle={handleSortToggle}
          />
        ),
        key: "positionScore",
        dataIndex: "positionScore",
        align: "center",
        render: (v: number) => (Number.isFinite(v) ? v.toFixed(1) : "—"),
      },
      {
        title: (
          <SortHeader
            label="股息率"
            field="dividendYield"
            active={sortMode === "dividendYield"}
            order={sortOrder}
            onToggle={handleSortToggle}
          />
        ),
        key: "dividendYield",
        dataIndex: "dividendYield",
        align: "center",
        render: (v: number) => <FinNum>{formatDividendYield(v)}</FinNum>,
      },
      {
        title: (
          <SortHeader
            label="总市值"
            field="marketValue"
            active={sortMode === "marketValue"}
            order={sortOrder}
            onToggle={handleSortToggle}
          />
        ),
        key: "marketValue",
        dataIndex: "marketValue",
        align: "center",
        render: (v: number) => <FinNum>{formatMarketValue(v)}</FinNum>,
      },
      {
        title: (
          <SortHeader
            label="流通市值"
            field="floatMarketValue"
            active={sortMode === "floatMarketValue"}
            order={sortOrder}
            onToggle={handleSortToggle}
          />
        ),
        key: "floatMarketValue",
        dataIndex: "floatMarketValue",
        align: "center",
        render: (v: number) => <FinNum>{formatMarketValue(v)}</FinNum>,
      },
      {
        title: (
          <SortHeader
            label="市盈率"
            field="peRatio"
            active={sortMode === "peRatio"}
            order={sortOrder}
            onToggle={handleSortToggle}
          />
        ),
        key: "peRatio",
        dataIndex: "peRatio",
        align: "center",
        render: (v: number) => <FinNum>{formatPeRatio(v)}</FinNum>,
      },
    ],
    [
      handleSortToggle,
      requestStockDetailForLink,
      buildStockNotesTabProps,
      sortMode,
      sortOrder,
      stockLinkDetail,
    ]
  );

  return (
    <div className="concept-page">
      <div className="concept-page__inner">
        <div className="concept-page__toolbar">
          <div className="concept-page__concepts">
            <ConceptDropSelect
              multiple
              conceptCodes={conceptCodes}
              conceptsCatalog={conceptOptions}
              onSelect={(c: ConceptSearchBrief) => {
                setConceptCodes((prev) =>
                  prev.some((x) => x === c.code) ? prev : [...prev, c.code]
                );
              }}
              onRemoveCode={(code: string) => {
                setConceptCodes((prev) => prev.filter((x) => x !== code));
              }}
              onCleared={() => setConceptCodes([])}
              onEnter={() => void runSearch()}
              placeholder="选择概念"
              borderRadius={4}
              backgroundColor={"rgb(255, 255, 255)"}
            />
          </div>
          <Button
            type="default"
            className="concept-page__search-btn"
            icon={
              <svg
                className="concept-page__search-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.75" />
                <path
                  d="M15.5 15.5L20 20"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            }
            onClick={() => void runSearch()}
          >
            搜索
          </Button>
        </div>

        <div className="concept-page__list-bar">
          <span className="concept-page__result-count">共{sortedRows.length}个结果</span>
          <button type="button" className="concept-page__dragon-btn" onClick={applyDragonSort}>
            恢复潜龙排序(默认)
          </button>
        </div>

        <Spin spinning={loading}>
          <Table<ConceptStockRow>
            className="concept-page__table"
            rowKey={(r) => r.stockCode}
            columns={columns}
            dataSource={pageRows}
            pagination={false}
            locale={{ emptyText: conceptCodes.length === 0 ? "请先选择概念" : "暂无数据" }}
          />
          {sortedRows.length > PAGE_SIZE ? (
            <div className="concept-page__pagination">
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={sortedRows.length}
                showSizeChanger={false}
                onChange={(p) => setPage(p)}
              />
            </div>
          ) : null}
        </Spin>
      </div>
    </div>
  );
}
